/**
 * Helper functions for AuditItemsFilters component
 *
 * Extracted to reduce component complexity and improve maintainability.
 */

// Import GraphQL types from proper domain location
import type { MultiSelectOption } from '@soteria/design-system-ve';

import type { GraphQLAuditItem } from '../../domain/graphql-types';

import type { AuditItemFilters, AuditItemFilterOptions, AuditItemPagination, PageSize } from './types';
import { AUDIT_ITEM_STATUS_FILTER_OPTIONS, MULTIPLE_DECISIONS_FILTER_OPTIONS, SEVERITY_FILTER_OPTIONS } from './types';

/**
 * Check if any filters are currently active
 */
export function hasActiveAuditItemFilters(filters: AuditItemFilters): boolean {
  return (
    !!filters.status ||
    !!filters.label ||
    filters.severity !== undefined ||
    !!filters.multipleDecisionsStatus ||
    (filters.violatesPolicy !== undefined && filters.violatesPolicy !== null)
  );
}

/**
 * Count the total number of active filters
 */
export function getActiveAuditItemFilterCount(filters: AuditItemFilters): number {
  let count = 0;

  if (filters.status) count++;
  if (filters.label) count++;
  if (filters.severity !== undefined) count++;
  if (filters.multipleDecisionsStatus) count++;
  if (filters.violatesPolicy !== undefined && filters.violatesPolicy !== null) count++;

  return count;
}

/**
 * Convert status options to MultiSelect format
 */
export function createStatusOptions(): MultiSelectOption[] {
  return AUDIT_ITEM_STATUS_FILTER_OPTIONS.map(option => ({
    id: option.id,
    label: option.label,
    value: option.value,
  }));
}

/**
 * Convert multiple decisions status options to MultiSelect format
 */
export function createMultipleDecisionsStatusOptions(): MultiSelectOption[] {
  return MULTIPLE_DECISIONS_FILTER_OPTIONS.map(option => ({
    id: option.id,
    label: option.label,
    value: option.value,
  }));
}

/**
 * Convert severity options to MultiSelect format
 */
export function createSeverityOptions(): MultiSelectOption[] {
  return SEVERITY_FILTER_OPTIONS.map(option => ({
    id: option.id,
    label: option.label,
    value: option.value,
  }));
}

/**
 * Convert string array to MultiSelect options
 */
export function createMultiSelectOptions(items: string[]): MultiSelectOption[] {
  return items.map(item => ({
    id: item,
    label: item,
    value: item,
  }));
}

/**
 * Create all filter options for MultiSelect components
 */
export function createAuditItemFilterOptions(filterOptions: AuditItemFilterOptions) {
  return {
    statusOptions: createStatusOptions(),
    labelOptions: createMultiSelectOptions(filterOptions.availableLabels),
    severityOptions: createSeverityOptions(),
    multipleDecisionsStatusOptions: createMultipleDecisionsStatusOptions(),
  };
}

/**
 * Check if audit item matches status filter
 */
function matchesStatusFilter(item: GraphQLAuditItem, statusFilter?: string): boolean {
  return !statusFilter || item.status === statusFilter;
}

/**
 * Check if audit item matches label filter
 */
function matchesLabelFilter(item: GraphQLAuditItem, labelFilter?: string): boolean {
  if (!labelFilter) return true;

  // Use GraphQL originalClassification.labels directly
  const itemLabels = item.contentSnapshot?.classificationResults?.map(result => result.name) || [];

  return itemLabels.includes(labelFilter);
}

/**
 * Check if audit item matches severity filter
 */
function matchesSeverityFilter(item: GraphQLAuditItem, severityFilter?: number): boolean {
  if (severityFilter === undefined) return true;

  // Extract severities from confidenceScores JSON string
  try {
    const scores =
      item.contentSnapshot?.classificationResults?.reduce(
        (acc, result) => {
          acc[result.name] = result.severity / 10; // Convert severity to confidence score

          return acc;
        },
        {} as Record<string, number>
      ) || {};
    const itemSeverities = Object.values(scores).map((score: unknown) => Math.round((Number(score) || 0) * 3) + 1);

    return itemSeverities.includes(severityFilter);
  } catch {
    return false;
  }
}

/**
 * Check if audit item matches conflict status filter
 * Note: This would need to be enhanced based on actual conflict detection logic
 */
function matchesMultipleDecisionsStatusFilter(item: GraphQLAuditItem, multipleDecisionsStatusFilter?: string): boolean {
  if (!multipleDecisionsStatusFilter) return true;

  const decisionsCount = item.decisionsCount;

  if (multipleDecisionsStatusFilter === 'single') {
    return decisionsCount === 1;
  } else if (multipleDecisionsStatusFilter === 'none') {
    return decisionsCount === 0;
  } else if (multipleDecisionsStatusFilter === 'multiple') {
    return decisionsCount > 1;
  }

  return true;
}

/**
 * Check if audit item matches policy violation filter
 */
function matchesPolicyViolationFilter(item: GraphQLAuditItem, violatesPolicy?: boolean): boolean {
  if (violatesPolicy === undefined) return true;
  const triggeredPolicies = item.contentSnapshot?.triggeredPolicies || [];
  const hasViolations = triggeredPolicies.length > 0;

  return violatesPolicy ? hasViolations : !hasViolations;
}

/**
 * Helper function to apply filters to audit items
 */
export function applyAuditItemFilters(items: GraphQLAuditItem[], filters: AuditItemFilters): GraphQLAuditItem[] {
  return items.filter(
    item =>
      matchesStatusFilter(item, filters.status) &&
      matchesLabelFilter(item, filters.label) &&
      matchesSeverityFilter(item, filters.severity) &&
      matchesMultipleDecisionsStatusFilter(item, filters.multipleDecisionsStatus) &&
      matchesPolicyViolationFilter(item, filters.violatesPolicy)
  );
}

/**
 * Extract filter options from audit items array
 */

// Helper function to extract labels from item
function extractLabelsFromItem(item: GraphQLAuditItem, labels: Set<string>): void {
  item.contentSnapshot?.classificationResults?.forEach(result => {
    const label = result.name;

    labels.add(label);
  });
}

// Helper function to extract severities from item
function extractSeveritiesFromItem(item: GraphQLAuditItem, severities: Set<number>): void {
  try {
    const scores =
      item.contentSnapshot?.classificationResults?.reduce(
        (acc, result) => {
          acc[result.name] = result.severity / 10; // Convert severity to confidence score

          return acc;
        },
        {} as Record<string, number>
      ) || {};

    Object.values(scores).forEach((score: unknown) => {
      const severity = Math.round((Number(score) || 0) * 3) + 1;

      severities.add(severity);
    });
  } catch {
    // Ignore parsing errors
  }
}

export function extractFilterOptionsFromItems(items: GraphQLAuditItem[]): AuditItemFilterOptions {
  const labels = new Set<string>();
  const severities = new Set<number>();

  items.forEach(item => {
    // Extract labels from GraphQL originalClassification
    extractLabelsFromItem(item, labels);

    // Extract severities from confidenceScores
    extractSeveritiesFromItem(item, severities);
  });

  return {
    availableLabels: Array.from(labels).sort(),
    availableSeverities: Array.from(severities).sort((a, b) => a - b),
    availableViolatesPolicyOptions: [true, false],
  };
}

/**
 * Apply pagination to audit items array
 */
export function paginateAuditItems(items: GraphQLAuditItem[], pagination: AuditItemPagination): GraphQLAuditItem[] {
  const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;

  return items.slice(startIndex, endIndex);
}

/**
 * Calculate total pages for pagination
 */
export function calculateTotalPages(totalItems: number, pageSize: PageSize): number {
  return Math.ceil(totalItems / pageSize);
}

/**
 * Get pagination info for display
 */
export function getPaginationInfo(totalItems: number, pagination: AuditItemPagination) {
  const totalPages = calculateTotalPages(totalItems, pagination.pageSize);
  const startIndex = (pagination.currentPage - 1) * pagination.pageSize + 1;
  const endIndex = Math.min(pagination.currentPage * pagination.pageSize, totalItems);
  const currentPageItems = endIndex - startIndex + 1;

  return {
    totalPages,
    startIndex,
    endIndex,
    currentPageItems,
  };
}
